type ChunkReloadState = {
  href: string;
  at: number;
};

const CHUNK_RELOAD_STATE_KEY = 'vite:chunk-reload-recovery';
const CHUNK_RELOAD_LOCK_MS = 15_000;

const clearChunkReloadState = () => {
  try {
    window.sessionStorage.removeItem(CHUNK_RELOAD_STATE_KEY);
  } catch {
    // Ignore storage failures and fall back to the default browser error flow.
  }
};

const readChunkReloadState = (): ChunkReloadState | null => {
  try {
    const rawState = window.sessionStorage.getItem(CHUNK_RELOAD_STATE_KEY);
    if (!rawState) return null;

    const parsedState = JSON.parse(rawState) as Partial<ChunkReloadState>;
    if (typeof parsedState.href !== 'string' || typeof parsedState.at !== 'number') {
      clearChunkReloadState();
      return null;
    }

    return {
      href: parsedState.href,
      at: parsedState.at,
    };
  } catch {
    clearChunkReloadState();
    return null;
  }
};

const writeChunkReloadState = (state: ChunkReloadState) => {
  try {
    window.sessionStorage.setItem(CHUNK_RELOAD_STATE_KEY, JSON.stringify(state));
  } catch {
    // Ignore storage failures and fall back to the default browser error flow.
  }
};

const hasRecentChunkReload = (href: string, now = Date.now()) => {
  const reloadState = readChunkReloadState();
  if (!reloadState) return false;

  if (reloadState.href !== href || now - reloadState.at >= CHUNK_RELOAD_LOCK_MS) {
    clearChunkReloadState();
    return false;
  }

  return true;
};

const scheduleChunkReloadStateCleanup = (href: string) => {
  window.setTimeout(() => {
    const reloadState = readChunkReloadState();
    if (!reloadState || reloadState.href !== href) return;

    if (Date.now() - reloadState.at >= CHUNK_RELOAD_LOCK_MS) {
      clearChunkReloadState();
    }
  }, CHUNK_RELOAD_LOCK_MS);
};

export const installChunkLoadRecovery = () => {
  if (typeof window === 'undefined') return;

  const currentHref = window.location.href;
  hasRecentChunkReload(currentHref);
  scheduleChunkReloadStateCleanup(currentHref);

  window.addEventListener('vite:preloadError', (event: Event) => {
    const href = window.location.href;
    if (hasRecentChunkReload(href)) return;

    event.preventDefault();
    writeChunkReloadState({
      href,
      at: Date.now(),
    });
    window.location.reload();
  });
};
